/* eslint-disable import/no-dynamic-require */
/* eslint-disable global-require */
/* eslint-disable no-nested-ternary */
import React, { useEffect, useState } from "react";
import SwipeableViews from "react-swipeable-views";
import { autoPlay } from "react-swipeable-views-utils";
import { Card } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { enqueueSnackbar } from "notistack";
import { useSelector } from "react-redux";
import _ from "lodash";
import MDTypography from "components/MDTypography";
import MDBox from "components/MDBox";
import { postAPICall } from "lib/callApi";
import { GET_TOP_PERFORMERS } from "lib/constants";
import { getFilterDates, numberFormatter } from "utils/HelperFunctions";
import DotPagination from "components/DotPagination";
import Loader from "utils/Loader";

const AutoPlayViews = autoPlay(SwipeableViews);
const prefix = process.env.REACT_APP_CDN_ASSET_PREFIX || "/assets";

const convertNameToInitials = (name) => {
  try {
    if (!name) return "";

    return name
      .split(" ")
      .map((sub) => sub.charAt(0).toUpperCase())
      .join("");
  } catch (err) {
    return "";
  }
};

/**
 * TopPerformer: It's a child component used to render top performer UI in Swipeable View
 * @param {*} performer Current performer data
 * @param {*} index the index of current performer
 */
const TopPerformer = React.memo(({ performerKey, columnName, topPerformers, setAutoplayState }) => (
  <div
    key={performerKey}
    onMouseEnter={() => setAutoplayState(false)}
    onMouseLeave={() => setAutoplayState(true)}
  >
    <MDTypography variant="h4" mb={2} align="center" paddingTop={2}>
      {topPerformers?.length ? `Top ${topPerformers.length}` : ""} {columnName}
    </MDTypography>
    <MDBox
      sx={{
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        width: "100%",
      }}
    >
      {topPerformers?.length ? (
        topPerformers.map((performer, index) => (
          <MDBox
            key={performer.User.name}
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              width: "100%",
            }}
          >
            <MDBox
              sx={{
                position: "relative",
                width: "250px",
                height: "220px",
              }}
            >
              <MDBox
                component="img"
                sx={{
                  zIndex: 1,
                  position: "absolute",
                  width: "180px",
                  height: "180px",
                  left: "50%",
                  transform: "translateX(-50%)",
                  top: "35px",
                }}
                src={
                  performer.User.profilePicture
                    ? `${prefix}/images/leaderboard/${(index || 0) + 1}-place-with-image.svg`
                    : `${prefix}/images/leaderboard/${(index || 0) + 1}-place.svg`
                }
                alt={performer.User.name}
              />
              {performer.User.profilePicture ? (
                <MDBox
                  component="img"
                  sx={{
                    position: "absolute",
                    width: "170px",
                    height: "170px",
                    zIndex: 2,
                    borderRadius: "50%",
                    left: "50%",
                    transform: "translateX(-50%)",
                    top: "40px",
                  }}
                  src={performer?.User?.profilePicture}
                  alt={performer.User.name}
                />
              ) : (
                <MDTypography
                  variant="h3"
                  sx={{
                    position: "absolute",
                    left: "50%",
                    transform: "translateX(-50%)",
                    top: "100px",
                    zIndex: 2,
                  }}
                >
                  {convertNameToInitials(performer.User.name)}
                </MDTypography>
              )}
            </MDBox>
            <MDTypography variant="h4" mt={0} mb={1}>
              {index + 1}
              <MDTypography variant="caption" sx={{ marginLeft: "2px", fontWeight: 800 }}>
                {index === 0 ? "st" : index === 1 ? "nd" : index ? "rd" : ""}
              </MDTypography>{" "}
              Place - {performer.User.name}
            </MDTypography>
            <MDTypography variant="body2" mb={1}>
              {numberFormatter(parseInt(performer[performerKey] || 0, 10))}
              {performerKey === "quotaAttainment" ? "%" : ""} - {columnName}
            </MDTypography>
          </MDBox>
        ))
      ) : (
        <MDBox
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            width: "100%",
            height: "100%",
            my: 8,
          }}
        >
          <MDTypography variant="h6">No users are found matching the selected metrics</MDTypography>
        </MDBox>
      )}
    </MDBox>
  </div>
));

const TopPerformers = ({
  filters,
  columns,
  tableFilterOptions,
  initialLoading,
  lastRefreshedOn,
  tableFilterOptionsLoading,
}) => {
  const theme = useTheme();
  const loginUserState = useSelector((state) => state.loginUser.loginUserState);

  const [activeStep, setActiveStep] = useState(0);
  const [autoplayState, setAutoplayState] = useState(true);

  const [dateFilterLoading, setDateFilterLoading] = useState(true);
  const [dateFilterObject, setDateFilterObject] = useState({
    dateFilter: "",
    startDate: null,
    endDate: null,
  });
  const [topPerformers, setTopPerformers] = useState({});
  const [loading, setLoading] = useState(true);

  /**
   * handleStepChange: Used to auto swipe or manual swipe current displayed performer
   * @param {*} step the step/index of current performer
   */
  const handleStepChange = (step, _fromStep, data) => {
    if (autoplayState || data?.reason === "swipe") setActiveStep(step);
  };

  /**
   * fetchTopPerformers: Use to fetch top performers list based on applied date filter
   */
  const fetchTopPerformers = async () => {
    if (initialLoading || dateFilterLoading || tableFilterOptionsLoading) return;
    setLoading(true);

    try {
      if (
        dateFilterObject.dateFilter === "Custom" &&
        (!dateFilterObject.startDate || !dateFilterObject.endDate)
      ) {
        enqueueSnackbar(
          JSON.stringify({
            icon: "error",
            title: "Error",
            text: "Select date interval!",
          }),
          {
            variant: "custom",
          }
        );
        return;
      }

      const payloadBody = {
        dateFilter: dateFilterObject.dateFilter,
        startDate: dateFilterObject.startDate,
        endDate: dateFilterObject.endDate,
        timezone: loginUserState.timezone,
        columns: {
          kpis: columns?.kpis?.filter((kpi) =>
            tableFilterOptions?.kpis?.some((kpiFilter) => kpiFilter?.id === kpi)
          ),
          other: columns?.other?.filter((other) =>
            tableFilterOptions?.other?.some((otherFilter) => otherFilter?.id === other)
          ),
        },
      };

      if (dateFilterObject.dateFilter !== "Custom") {
        const dates = getFilterDates(
          dateFilterObject.dateFilter,
          loginUserState.timezone,
          loginUserState.fiscalYearStart
        );

        payloadBody.startDate = dates.startDate;
        payloadBody.endDate = dates.endDate;
      }

      const topPerformersData = await postAPICall(GET_TOP_PERFORMERS, true, payloadBody);

      if (topPerformersData?.data?.success) {
        const newTopPerformers = topPerformersData?.data?.data || {};
        setTopPerformers(newTopPerformers);
        if (Object.keys(newTopPerformers).length <= activeStep) setActiveStep(0);
      }
    } catch (error) {
      enqueueSnackbar(
        JSON.stringify({
          icon: "error",
          title: "Error",
          text: error?.response?.data?.message || error.message || "Could not retrieve data!",
        }),
        {
          variant: "custom",
        }
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTopPerformers();
  }, [
    dateFilterObject,
    columns,
    tableFilterOptions,
    lastRefreshedOn,
    initialLoading,
    dateFilterLoading,
    tableFilterOptionsLoading,
  ]);

  useEffect(() => {
    setDateFilterLoading(true);
    const newDateFilterObject = {
      dateFilter: filters.dateFilter,
      startDate: filters.startDate,
      endDate: filters.endDate,
    };

    // Did't update filter objet if nothing changed
    if (!_.isEqual(dateFilterObject, newDateFilterObject)) {
      setDateFilterObject(newDateFilterObject);
    }
    setDateFilterLoading(false);
  }, [filters]);

  const findColumnName = (performerKey) =>
    performerKey
      ? (performerKey.startsWith("kpi-")
          ? tableFilterOptions?.kpis?.find(
              (kpi) => kpi.id === Number(performerKey.split("kpi-")[1] || 0)
            )
          : tableFilterOptions?.other?.find((kpi) => kpi.id === performerKey)
        )?.name
      : "";

  return (
    <Card sx={{ padding: "3px" }}>
      <Loader loading={loading} />
      {Object.keys(topPerformers)?.length ? (
        <>
          <AutoPlayViews
            axis={theme.direction === "rtl" ? "x-reverse" : "x"}
            index={activeStep}
            onChangeIndex={handleStepChange}
            enableMouseEvents
            interval={5000} // 5 seconds interval
          >
            {Object.keys(topPerformers).map((performerKey) => {
              const columnName = findColumnName(performerKey) || "";
              return (
                <TopPerformer
                  key={performerKey}
                  columnName={columnName}
                  topPerformers={topPerformers[performerKey]}
                  performerKey={performerKey}
                  setAutoplayState={setAutoplayState}
                />
              );
            })}
          </AutoPlayViews>
          <DotPagination
            dots={Object.keys(topPerformers).length}
            index={activeStep}
            onChangeIndex={handleStepChange}
          />
        </>
      ) : (
        <MDBox
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            width: "100%",
            height: "100%",
          }}
        >
          <MDTypography variant="h6">No users are found matching the selected metrics</MDTypography>
        </MDBox>
      )}
    </Card>
  );
};

export default TopPerformers;
